/**
 * Sites store — decoupled per-section site dashboard fetcher.
 *
 * Each dashboard section is fetched independently so the page can render
 * available sections immediately while others continue loading.
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";
import { getLogger } from "@/lib/logging";

const logger = getLogger("opus.sites-store");
import {
  AssuranceAlertListSchema,
  AlertHistoryListSchema,
  ConnectedClientListSchema,
  DeviceClassRollupSchema,
  FirmwareUpgradeListSchema,
  SiteLicenseListSchema,
  SiteDeviceListSchema,
  SiteSummarySchema,
  SiteWanUplinkSchema,
  ThroughputSeriesSchema,
  WirelessFailedConnectionListSchema,
  type SiteDashboard,
  type SiteSummary,
} from "@/api/schemas/sites";
import { MOCK_SITE_DASHBOARDS, MOCK_SITE_SUMMARIES } from "@/api/fixtures/sites";
import { z } from "zod";

const FRESHNESS_MS = 30_000;

type DashboardSectionKey =
  | "summary"
  | "rollups"
  | "wanUplinks"
  | "throughput"
  | "clientBandwidthUsage"
  | "devices"
  | "clients"
  | "firmwareUpgrades"
  | "alertsHistory"
  | "licenses"
  | "assuranceAlerts"
  | "wirelessFailedConnections";

type SectionStatus = "idle" | "loading" | "ready" | "error";

type SectionStatusMap = Record<DashboardSectionKey, SectionStatus>;

const EMPTY_SECTION_STATUS: SectionStatusMap = {
  summary: "idle",
  rollups: "idle",
  wanUplinks: "idle",
  throughput: "idle",
  clientBandwidthUsage: "idle",
  devices: "idle",
  clients: "idle",
  firmwareUpgrades: "idle",
  alertsHistory: "idle",
  licenses: "idle",
  assuranceAlerts: "idle",
  wirelessFailedConnections: "idle",
};

interface CachedDashboard extends Partial<SiteDashboard> {
  _sectionFetchedAt?: Partial<Record<DashboardSectionKey, number>>;
}

interface SectionRequest {
  key: DashboardSectionKey;
  path: string;
  pathFallbacks?: string[];
  query?: Record<string, string | number | boolean | undefined | null>;
  parse: (raw: unknown) => unknown;
  assign: (raw: unknown) => Partial<SiteDashboard>;
}

// Try each path in sequence, returning on the first success.
// Falls through to the next only when the error is a 404 — any other
// status bubbles up immediately.
async function tryPathsInSequence(
  paths: readonly string[],
  query: Record<string, string | number | boolean | undefined | null> | undefined,
): Promise<{ raw: unknown; resolvedPath: string }> {
  const [first, ...rest] = paths;
  if (!first) throw new Error("No paths to try");
  try {
    const raw = await apiGet<unknown>(first, query);
    return { raw, resolvedPath: first };
  } catch (err) {
    if (rest.length === 0 || !(err instanceof ApiError) || err.status !== 404) {
      throw err;
    }
    return tryPathsInSequence(rest, query);
  }
}

interface DashboardLoadOptions {
  force?: boolean;
  clientsQuery?: Record<string, string | number | boolean | undefined | null>;
  timeseriesQuery?: Record<string, string | number | boolean | undefined | null>;
}

const ALL_DASHBOARD_SECTIONS: DashboardSectionKey[] = [
  "summary",
  "rollups",
  "wanUplinks",
  "throughput",
  "clientBandwidthUsage",
  "devices",
  "clients",
  "firmwareUpgrades",
  "alertsHistory",
  "licenses",
  "assuranceAlerts",
  "wirelessFailedConnections",
];

function sectionRequestFor(
  slug: string,
  section: DashboardSectionKey,
  opts?: DashboardLoadOptions,
): SectionRequest {
  if (section === "summary") {
    return {
      key: "summary",
      path: `/sites/${slug}/summary/`,
      parse: (raw) => SiteSummarySchema.parse(raw),
      assign: (raw) => ({ summary: raw as SiteDashboard["summary"] }),
    };
  }
  if (section === "rollups") {
    return {
      key: "rollups",
      path: `/sites/${slug}/rollups/`,
      parse: (raw) => z.array(DeviceClassRollupSchema).parse(raw),
      assign: (raw) => ({ rollups: raw as SiteDashboard["rollups"] }),
    };
  }
  if (section === "wanUplinks") {
    return {
      key: "wanUplinks",
      path: `/sites/${slug}/wan-uplinks/`,
      parse: (raw) => z.array(SiteWanUplinkSchema).parse(raw),
      assign: (raw) => ({ wanUplinks: raw as SiteDashboard["wanUplinks"] }),
    };
  }
  if (section === "throughput") {
    return {
      key: "throughput",
      path: `/sites/${slug}/throughput/`,
      query: opts?.timeseriesQuery,
      parse: (raw) =>
        z
          .object({ throughput: ThroughputSeriesSchema })
          .passthrough()
          .parse(raw),
      assign: (raw) => ({
        throughput: (raw as { throughput: SiteDashboard["throughput"]; }).throughput,
      }),
    };
  }
  if (section === "clientBandwidthUsage") {
    return {
      key: "clientBandwidthUsage",
      path: `/sites/${slug}/client-bandwidth-usage/`,
      pathFallbacks: [`/sites/${slug}/client-bandwidth-usage`],
      query: opts?.timeseriesQuery,
      parse: (raw) =>
        z
          .object({ clientBandwidthUsage: ThroughputSeriesSchema })
          .passthrough()
          .parse(raw),
      assign: (raw) => ({
        clientBandwidthUsage: (
          raw as { clientBandwidthUsage: SiteDashboard["clientBandwidthUsage"]; }
        ).clientBandwidthUsage,
      }),
    };
  }
  if (section === "devices") {
    return {
      key: "devices",
      path: `/sites/${slug}/devices/`,
      parse: (raw) => SiteDeviceListSchema.parse(raw),
      assign: (raw) => ({ devices: raw as SiteDashboard["devices"] }),
    };
  }
  if (section === "clients") {
    return {
      key: "clients",
      path: `/sites/${slug}/clients/`,
      pathFallbacks: [`/sites/${slug}/clients`],
      query: opts?.clientsQuery,
      parse: (raw) => ConnectedClientListSchema.parse(raw),
      assign: (raw) => ({ clients: raw as SiteDashboard["clients"] }),
    };
  }
  if (section === "firmwareUpgrades") {
    return {
      key: "firmwareUpgrades",
      path: `/sites/${slug}/firmware-upgrades/`,
      pathFallbacks: [
        `/sites/${slug}/firmware-upgrades`,
        `/sites/${slug}/firmware-upgrade/`,
        `/sites/${slug}/firmware-upgrade`,
        `/sites/${slug}/firmware/`,
        `/sites/${slug}/firmware`,
      ],
      parse: (raw) => FirmwareUpgradeListSchema.parse(raw),
      assign: (raw) => ({
        firmwareUpgrades: raw as SiteDashboard["firmwareUpgrades"],
      }),
    };
  }
  if (section === "assuranceAlerts") {
    return {
      key: "assuranceAlerts",
      path: `/sites/${slug}/assurance-alerts/`,
      pathFallbacks: [`/sites/${slug}/assurance-alerts`],
      query: opts?.timeseriesQuery,
      parse: (raw) => AssuranceAlertListSchema.parse(raw),
      assign: (raw) => ({
        assuranceAlerts: raw as SiteDashboard["assuranceAlerts"],
      }),
    };
  }
  if (section === "licenses") {
    return {
      key: "licenses",
      path: `/sites/${slug}/licenses/`,
      pathFallbacks: [`/sites/${slug}/licenses`],
      parse: (raw) => SiteLicenseListSchema.parse(raw),
      assign: (raw) => ({
        licenses: raw as SiteDashboard["licenses"],
      }),
    };
  }
  if (section === "wirelessFailedConnections") {
    return {
      key: "wirelessFailedConnections",
      path: `/sites/${slug}/wireless-failed-connections/`,
      pathFallbacks: [`/sites/${slug}/wireless-failed-connections`],
      query: opts?.timeseriesQuery,
      parse: (raw) => WirelessFailedConnectionListSchema.parse(raw),
      assign: (raw) => ({
        wirelessFailedConnections: raw as SiteDashboard["wirelessFailedConnections"],
      }),
    };
  }
  return {
    key: "alertsHistory",
    path: `/sites/${slug}/alerts-history/`,
    pathFallbacks: [
      `/sites/${slug}/alerts-history`,
      `/sites/${slug}/alert-history/`,
      `/sites/${slug}/alert-history`,
      `/sites/${slug}/alerts/`,
      `/sites/${slug}/alerts`,
    ],
    parse: (raw) => AlertHistoryListSchema.parse(raw),
    assign: (raw) => ({ alertsHistory: raw as SiteDashboard["alertsHistory"] }),
  };
}

interface State {
  /** Per-site dashboard cache, keyed by slug. */
  bySlug: Record<string, CachedDashboard>;
  sectionStatusBySlug: Record<string, SectionStatusMap>;
  /** All known sites — populated from /api/sites/. Drives the picker + list. */
  sites: SiteSummary[];
  /** ISO timestamp of when the backend last cached the sites list (null = just fetched live). */
  sitesListCachedAt: string | null;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  loadSites: (opts?: { includeOther?: boolean; includeTest?: boolean; }) => Promise<void>;
  loadDashboard: (slug: string, opts?: DashboardLoadOptions) => Promise<void>;
  loadDashboardSection: (
    slug: string,
    section: DashboardSectionKey,
    opts?: DashboardLoadOptions,
  ) => Promise<void>;
}

export const useSites = create<State>((set, get) => ({
  bySlug: {},
  sectionStatusBySlug: {},
  sites: [],
  sitesListCachedAt: null,
  status: "idle",
  error: null,

  loadSites: async (opts) => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    const includeOther = Boolean(opts?.includeOther);
    const includeTest = Boolean(opts?.includeTest);
    const t0 = performance.now();
    logger.info({ includeOther, includeTest }, "sites_load_start");
    try {
      const raw = await apiGet<unknown>("/sites/", { includeOther, includeTest });
      // Parse both the legacy bare-array format and the new {sites, cachedAt} envelope.
      let siteList: unknown[];
      let cachedAt: string | null = null;
      if (Array.isArray(raw)) {
        siteList = raw;
      } else if (raw && typeof raw === "object" && "sites" in raw) {
        siteList = ((raw as Record<string, unknown>).sites as unknown[]) ?? [];
        cachedAt = ((raw as Record<string, unknown>).cachedAt as string | null) ?? null;
      } else {
        throw new Error("unexpected response shape from /sites/");
      }
      set({ sites: siteList as SiteSummary[], sitesListCachedAt: cachedAt, status: "ready" });
      logger.info({ count: siteList.length, cached_at: cachedAt, elapsed_ms: Math.round(performance.now() - t0) }, "sites_load_complete");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      logger.warn({ error: message, elapsed_ms: Math.round(performance.now() - t0), falling_back_to_fixtures: true }, "sites_load_error");
      const fixtureSites = MOCK_SITE_SUMMARIES.filter((s) => {
        const vendor = (s.vendor ?? "").toLowerCase();
        const hasVendorAssociation = vendor.includes("meraki") || vendor.includes("aruba");
        const keepOther = includeOther || hasVendorAssociation;
        const keepTest = includeTest || !s.isTest;
        return keepOther && keepTest;
      });
      set({
        sites: fixtureSites,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  loadDashboardSection: async (slug, section, opts) => {
    const fallback = MOCK_SITE_DASHBOARDS[slug] ?? MOCK_SITE_DASHBOARDS.wasci;
    const fetchedAt = get().bySlug[slug]?._sectionFetchedAt ?? {};
    const req = sectionRequestFor(slug, section, opts);

    if (!opts?.force) {
      const last = fetchedAt[section];
      if (last && Date.now() - last < FRESHNESS_MS) {
        logger.debug({ slug, section, age_ms: Date.now() - last }, "dashboard_section_cache_hit");
        return;
      }
    }

    set((s) => ({
      sectionStatusBySlug: {
        ...s.sectionStatusBySlug,
        [slug]: {
          ...(s.sectionStatusBySlug[slug] ?? EMPTY_SECTION_STATUS),
          [section]: "loading",
        },
      },
      error: null,
    }));

    const t0 = performance.now();
    try {
      const candidates = [req.path, ...(req.pathFallbacks ?? [])];
      const query = opts?.force
        ? { ...(req.query ?? {}), force: true }
        : req.query;
      const { raw, resolvedPath } = await tryPathsInSequence(candidates, query);
      const parsed = req.parse(raw);
      logger.info({ slug, section, path: resolvedPath, elapsed_ms: Math.round(performance.now() - t0) }, "dashboard_section_loaded");
      set((s) => ({
        bySlug: {
          ...s.bySlug,
          [slug]: {
            ...(s.bySlug[slug] ?? {}),
            ...req.assign(parsed),
            _sectionFetchedAt: {
              ...(s.bySlug[slug]?._sectionFetchedAt ?? {}),
              [section]: Date.now(),
            },
          },
        },
        sectionStatusBySlug: {
          ...s.sectionStatusBySlug,
          [slug]: {
            ...(s.sectionStatusBySlug[slug] ?? EMPTY_SECTION_STATUS),
            [section]: "ready",
          },
        },
        status: "ready",
      }));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      logger.warn({ slug, section, error: message, elapsed_ms: Math.round(performance.now() - t0), falling_back_to_fixtures: true }, "dashboard_section_error");
      const fallbackRaw =
        section === "throughput"
          ? { throughput: fallback.throughput }
          : section === "clientBandwidthUsage"
            ? { clientBandwidthUsage: fallback.clientBandwidthUsage }
            : section === "licenses"
              ? fallback.licenses
              : section === "assuranceAlerts"
                ? fallback.assuranceAlerts
                : section === "wirelessFailedConnections"
                  ? fallback.wirelessFailedConnections
                  : fallback[section];
      set((s) => ({
        bySlug: {
          ...s.bySlug,
          [slug]: {
            ...(s.bySlug[slug] ?? {}),
            ...(req.assign(fallbackRaw as unknown) as Partial<SiteDashboard>),
            _sectionFetchedAt: {
              ...(s.bySlug[slug]?._sectionFetchedAt ?? {}),
              [section]: Date.now(),
            },
          },
        },
        sectionStatusBySlug: {
          ...s.sectionStatusBySlug,
          [slug]: {
            ...(s.sectionStatusBySlug[slug] ?? EMPTY_SECTION_STATUS),
            [section]: "error",
          },
        },
        status: "error",
        error: `${message} — showing fixture data for ${section}`,
      }));
    }
  },

  loadDashboard: async (slug, opts) => {
    await Promise.all(
      ALL_DASHBOARD_SECTIONS.map((section) =>
        get().loadDashboardSection(slug, section, opts),
      ),
    );
  },
}));
